import React, { useState } from "react";
import { Alert, Button, IconButton, Snackbar, Tooltip, type ButtonProps } from "@mui/material";
import { Download as DownloadIcon } from "@mui/icons-material";
import { useExport } from "./ExportProvider";
import type { ExportConfig } from "./types";
import { useTranslation } from "react-i18next";

export type ExportButtonProps = {
  /** Omit when the view has no export yet; clicking then shows "Próximamente". */
  config?: ExportConfig | undefined;
  /** Hide the button entirely (e.g. user lacks export permission). */
  canExport?: boolean;
  label?: string;
  tooltip?: string;
  disabled?: boolean;
  variant?: ButtonProps["variant"];
  size?: ButtonProps["size"];
  sx?: ButtonProps["sx"];
  iconOnly?: boolean;
};

const ExportButton: React.FC<ExportButtonProps> = ({
  config,
  canExport = true,
  label,
  tooltip,
  disabled = false,
  variant = "outlined",
  size = "small",
  sx,
  iconOnly = false,
}) => {
  const { t } = useTranslation("common");
  const buttonLabel = label ?? t("export.button");
  const { open } = useExport();
  const [comingSoonOpen, setComingSoonOpen] = useState(false);
  const [tooltipOpen, setTooltipOpen] = useState(false);

  if (!canExport) return null;

  const tip = tooltip ?? (iconOnly ? buttonLabel : undefined);
  const onClick = () => {
    setTooltipOpen(false);
    if (config) void open(config);
    else setComingSoonOpen(true);
  };
  const button = iconOnly ? (
    <IconButton
      size={size}
      disabled={disabled}
      onClick={onClick}
      aria-label={buttonLabel}
      sx={sx}
    >
      <DownloadIcon fontSize="small" />
    </IconButton>
  ) : (
    <Button
      variant={variant}
      size={size}
      startIcon={<DownloadIcon />}
      disabled={disabled}
      onClick={onClick}
      sx={sx}
    >
      {buttonLabel}
    </Button>
  );

  return (
    <>
      {tip ? (
        <Tooltip
          title={tip}
          arrow
          open={tooltipOpen}
          onOpen={() => setTooltipOpen(true)}
          onClose={() => setTooltipOpen(false)}
          disableFocusListener
        >
          <span style={{ display: "inline-flex" }}>{button}</span>
        </Tooltip>
      ) : (
        button
      )}
      <Snackbar
        open={comingSoonOpen}
        autoHideDuration={3000}
        onClose={() => setComingSoonOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity="info"
          variant="filled"
          onClose={() => setComingSoonOpen(false)}
          sx={{
            alignItems: "center",
            "& .MuiAlert-icon, & .MuiAlert-message": { py: 0 },
            "& .MuiAlert-action": { pt: 0, py: 0, alignItems: "center" },
          }}
        >
          {t("export.comingSoon")}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ExportButton;
